// -----------------------------------------
// FIELD TYPE
//
// Cada campo do model pode ter propriedade opcional:
//   tooltip?: string  — quando preenchida, exibe ícone de informação ao lado do label no formulário de cadastro/edição (hover mostra o texto).
// -----------------------------------------

const bem_particular_tooltip = `No regime de comunhão parcial de bens, os bens particulares — que não se comunicam e pertencem exclusivamente a cada cônjuge — abrangem os adquiridos antes do casamento/união, heranças, doações, bens sub-rogados, instrumentos de profissão, itens de uso pessoal, proventos do trabalho e rendas similares. São patrimônios incomunicáveis, conforme Artigo 1.659 do Código Civil.`;

/** Ajuda contextual — cadastro de imóvel (óbito × atualizado × escritura/IR). */
const imovel_venal_obito_tooltip = `Usado no ITCMD Causa Mortis (transmissão pelo falecimento) e como referência do valor venal na data da abertura da sucessão. Em SP, alíquota fixa de 4%.`;
const imovel_venal_atualizado_tooltip = `Referência ao valor venal na data do ato ou da simulação. Entra nos emolumentos de Tabelião de Notas (escritura), no ITCMD Doação (quando houver liberalidade) e no Registro de Imóveis. Costuma ser informado pelo IPTU/Carnê atualizado.`;
const imovel_valor_base_escritura_tooltip = `(Opcional) Valor declarado no instrumento — tipicamente o valor histórico declarado no IR do meeiro. Usado APENAS no modo de partilha amigável "Acordo com doação derivada e usufruto" (cônjuge meeiro recebe usufruto + bens; herdeiro recebe nua-propriedade). Não afeta ITCMD Causa Mortis nem ITCMD Doação. Quando vazio, o sistema cai para o valor atualizado e depois para o valor de óbito.`;
const imovel_valor_separator_desc = `Você pode informar até três valores diferentes para o mesmo imóvel — cada um alimenta uma etapa do cálculo:
(1) óbito → ITCMD Causa Mortis;
(2) atualizado → ITCMD Doação, emolumentos e RI;
(3) escritura/IR → quinhão real em partilha amigável extrajudicial (opcional).`;

const veiculo_valor_mercado_tooltip = `Valor de mercado na data do óbito (ex.: tabela FIPE daquela data). Usado no ITCMD Causa Mortis e na composição do espólio.`;
const veiculo_valor_base_escritura_tooltip = `(Opcional) Valor declarado no instrumento — usado APENAS no modo de partilha amigável "Acordo com doação derivada e usufruto". Quando o veículo vai integralmente ao cônjuge meeiro em pagamento da meação, este valor compõe o quinhão real.`;
const veiculo_valor_separator_desc = `Informe o valor de mercado na data do óbito. Opcionalmente, informe também o valor declarado na escritura/IR para a partilha amigável extrajudicial.`;

const conta_saldo_data_obito_tooltip = `Saldo na data do óbito. Compõe a base do ITCMD Causa Mortis e do espólio na data da abertura da sucessão.`;
const conta_valor_base_escritura_tooltip = `(Opcional) Saldo declarado no instrumento — usado APENAS no modo de partilha amigável "Acordo com doação derivada e usufruto". Para contas, normalmente coincide com o saldo informado acima.`;

const doacao_valor_tooltip = `VALOR CHEIO do bem (não a parte doada). Em liberalidades com reserva de usufruto, o sistema aplica automaticamente o split nua-propriedade × usufruto (SP: ⅔/⅓) para definir a base do ITCMD Doação e a porção excluída do espólio.`;
const doacao_percentual_bem_tooltip = `Em liberalidades com reserva de usufruto, o art. 11, §2º do Dec. 45.837/SP fixa ⅔ do valor para a nua-propriedade e ⅓ para o usufruto. Este campo é preenchido automaticamente com ⅔ (66,67%) e usado tanto na exclusão do bem do espólio quanto na base do ITCMD Doação.`;
const doacao_donatarios_desc = `Doador: o(a) de cujus. Todos os familiares cadastrados aparecem aqui — defina a PORCENTAGEM da liberalidade que cada um recebe. Quem não recebe nada nesta doação fica com 0% e não é considerado donatário (não gera ITCMD nem colação). A soma das porcentagens deve totalizar 100%.`;

export enum FieldType {
    Separator = 'separator',
    String = 'string',
    Password = 'password',
    LongString = 'longString',
    Email = 'email',
    Number = 'number',
    Money = 'money',
    Percentage = 'percentage',
    List = 'list',
    Options = 'options',
    BemRelacionado = 'bemRelacionado',
    Relation = 'relation',
    Address = 'address',
    DoacaoPessoaInventario = 'doacaoPessoaInventario',
    /** Repartição visual ⅔ nua / ⅓ usufruto (SP) a partir do valor da liberalidade */
    DoacaoUsufrutoSpSplit = 'doacaoUsufrutoSpSplit',
    InternationalAddress = 'internacionalAddress',
    Date = 'date',
    Boolean = 'boolean',
}

// -----------------------------------------
// SCHEMAS

export const ModelUsuario = [
    {
        "field": "id",
        "label": "ID",
        "type": FieldType.Number,
        "required": true,
        "visible": false,
        "defaultValue": null,
    },
    {
        "field": "id_plano",
        "label": "Plano",
        "type": FieldType.Number,
        "required": true,
        "visible": false,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": null,
    },
    {
        "field": "nome",
        "label": "Nome",
        "type": FieldType.String,
        "required": true,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": "",
    },
    {
        "field": "email",
        "label": "Email",
        "type": FieldType.Email,
        "required": true,
        "visible": false,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": "",
    },
    {
        "field": "fone",
        "label": "Telefone",
        "type": FieldType.String,
        "required": true,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": "",
    },
    {
        "field": "endereco",
        "label": "Endereço",
        "type": FieldType.Address,
        "required": true,
        "visible": true,
        "sizeMD": 12,
        "sizeSM": 12,
        "defaultValue": "",
    },
    {
        "field": "senha",
        "label": "Senha",
        "type": FieldType.String,
        "required": false,
        "visible": false,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": "",
    },
    {
        "field": "funcao",
        "label": "Função",
        "type": FieldType.String,
        "required": true,
        "visible": false,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": "",
    },
]

export const ModelUsuarioSenha = [
    {
        "field": "id",
        "label": "ID",
        "type": FieldType.Number,
        "required": true,
        "visible": false,
        "defaultValue": null,
    },
    {
        "field": "senha_atual",
        "label": "Senha Atual",
        "type": FieldType.Password,
        "required": true,
        "visible": true,
        "sizeMD": 4,
        "sizeSM": 12,
        "defaultValue": "",
    },
    {
        "field": "senha",
        "label": "Nova Senha",
        "type": FieldType.Password,
        "required": true,
        "visible": true,
        "sizeMD": 4,
        "sizeSM": 12,
        "defaultValue": "",
    },
    {
        "field": "senha_confirmacao",
        "label": "Repita a nova senha",
        "type": FieldType.Password,
        "required": true,
        "visible": true,
        "sizeMD": 4,
        "sizeSM": 12,
        "defaultValue": "",
    },
]

export const ModelCobranca = [
    {
        "field": "id",
        "label": "ID",
        "type": FieldType.Number,
        "required": true,
        "visible": false,
        "defaultValue": null,
    },
    {
        "field": "id_plano",
        "label": "Plano",
        "type": FieldType.Number,
        "required": true,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": null,
    },
    {
        "field": "valor",
        "label": "Valor",
        "type": FieldType.Money,
        "required": true,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": 0,
    },
    {
        "field": "data_vencimento",
        "label": "Data de Vencimento",
        "type": FieldType.Date,
        "required": true,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": null,
    },
    {
        "field": "data_pagamento",
        "label": "Data de Pagamento",
        "type": FieldType.Date,
        "required": false,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": null,
    },
    {
        "field": "status",
        "label": "Status",
        "type": FieldType.Options,
        "required": true,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "options": ["Pendente", "Pago", "Atrasado", "Cancelado"],
        "defaultValue": "Pendente",
    },
]

export const ModelPlano = [
    {
        "field": "id",
        "label": "ID",
        "type": FieldType.Number,
        "required": true,
        "visible": false,
        "defaultValue": null,
    },
    {
        "field": "nome",
        "label": "Nome",
        "type": FieldType.String,
        "required": true,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": "",
    },
    {
        "field": "valor",
        "label": "Valor",
        "type": FieldType.Money,
        "required": true,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": 0,
    },
    {
        "field": "descricao",
        "label": "Descrição",
        "type": FieldType.String,
        "required": true,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": "",
    },
]

export const ModelEndereco = [
    {
        "field": "rua",
        "label": "Rua",
        "type": FieldType.String,
        "required": true,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": "",
    },
    {
        "field": "cidade",
        "label": "Cidade",
        "type": FieldType.String,
        "required": true,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": "",
    },
    {
        "field": "estado",
        "label": "Estado",
        "type": FieldType.String,
        "required": true,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": "",
    },
    {
        "field": "pais",
        "label": "País",
        "type": FieldType.String,
        "required": true,
        "visible": false,
        "defaultValue": "",
    },
    {
        "field": "cep",
        "label": "CEP",
        "type": FieldType.String,
        "required": true,
        "visible": false,
        "defaultValue": "",
    },
]

export const ModelPessoaDeCujus = [
    {
        "field": "id",
        "label": "ID",
        "type": FieldType.Number,
        "required": false,
        "visible": false,
        "defaultValue": null,
    },
    {
        "field": "nome",
        "label": "Nome",
        "type": FieldType.String,
        "required": true,
        "visible": true,
        "sizeMD": 12,
        "sizeSM": 12,
        "defaultValue": "",
    },
    {
        "field": "data_nascimento",
        "label": "Data de Nascimento",
        "type": FieldType.Date,
        "required": true,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": null,
    },
    {
        "field": "data_falecimento",
        "label": "Data de Falecimento",
        "type": FieldType.Date,
        "required": true,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": null,
    },
    {
        "field": "cpf",
        "label": "CPF",
        "type": FieldType.String,
        "required": false,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": "",
    },
    {
        "field": "rg",
        "label": "RG",
        "type": FieldType.String,
        "required": false,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": "",
    },
    {
        "field": "profissao",
        "label": "Profissão",
        "type": FieldType.String,
        "required": false,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": "",
    },
    {
        "field": "nacionalidade",
        "label": "Nacionalidade",
        "type": FieldType.String,
        "required": false,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": "Brasileira",
    },
    {
        "field": "endereco",
        "label": "Endereço / Local do Óbito",
        "type": FieldType.Address,
        "required": false,
        "visible": true,
        "sizeMD": 12,
        "sizeSM": 12,
        "defaultValue": "",
    },
    {
        "field": "tipo_declaracao_ir",
        "label": "Tipo de Declaração IR",
        "type": FieldType.Options,
        "required": false,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "options": [
            "Simples",
            "Completa",
        ],
        "defaultValue": "Simples",
    },
    {
        "field": "estado_civil",
        "label": "Estado Civil (antes do falecimento)",
        "type": FieldType.Options,
        "required": true,
        "visible": true,
        "sizeMD": 12,
        "sizeSM": 12,
        "options": [
            {
                "label": "Casado(a)",
                "value": "casado",
            },
            {
                "label": "Divorciado(a)",
                "value": "divorciado",
            },
            {
                "label": "União estável",
                "value": "uniao_estavel",
            },
            {
                "label": "Solteiro(a)",
                "value": "solteiro",
            },
            {
                "label": "Viúvo(a)",
                "value": "viuvo",
            },
        ],
        "defaultValue": "solteiro",
    },
    {
        "field": "casamento_regime",
        "label": "Regime de Bens",
        "type": FieldType.Options,
        "required": true,
        "visible": "estado_civil === 'casado' || estado_civil === 'uniao_estavel'",
        "sizeMD": 6,
        "sizeSM": 12,
        "options": [
            "Comunhão Universal de Bens",
            "Comunhão Parcial de Bens",
            "Separação Total de Bens",
            "Participação Final nos Aquestos",
            "Regime de Separação Obrigatória dos Bens",
        ],
        "defaultValue": "Comunhão Parcial de Bens",
    },
    {
        "field": "casamento_data",
        "label": "Data Casamento / União Estável",
        "type": FieldType.Date,
        "required": true,
        "visible": "estado_civil === 'casado' || estado_civil === 'uniao_estavel'",
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": null,
    },
]

export const ModelCliente = [
    {
        "field": "id",
        "label": "ID",
        "type": FieldType.Number,
        "required": false,
        "visible": false,
        "defaultValue": null,
    },
    {
        "field": "nome",
        "label": "Cliente",
        "type": FieldType.String,
        "required": true,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": "",
    },
    {
        "field": "email",
        "label": "Email",
        "type": FieldType.Email,
        "required": false,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": "",
    },
    {
        "field": "fone",
        "label": "Telefone",
        "type": FieldType.String,
        "required": false,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": "",
    },
    {
        "field": "cpf",
        "label": "CPF",
        "type": FieldType.String,
        "required": false,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": "",
    },
    {
        "field": "rg",
        "label": "RG",
        "type": FieldType.String,
        "required": false,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": "",
    },
    {
        "field": "profissao",
        "label": "Profissão",
        "type": FieldType.String,
        "required": false,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": "",
    },
    {
        "field": "nacionalidade",
        "label": "Nacionalidade",
        "type": FieldType.String,
        "required": false,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": "Brasileira",
    },
    {
        "field": "estado_civil",
        "label": "Estado Civil",
        "type": FieldType.Options,
        "required": false,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "options": [
            {
                "label": "Casado(a)",
                "value": "casado",
            },
            {
                "label": "Divorciado(a)",
                "value": "divorciado",
            },
            {
                "label": "União estável",
                "value": "uniao_estavel",
            },
            {
                "label": "Solteiro(a)",
                "value": "solteiro",
            },
            {
                "label": "Viúvo(a)",
                "value": "viuvo",
            },
        ],
        "defaultValue": "",
    },
    {
        "field": "endereco",
        "label": "Endereço",
        "type": FieldType.Address,
        "required": false,
        "visible": true,
        "sizeMD": 12,
        "sizeSM": 12,
        "defaultValue": "",
    },
    {
        "field": "vinculo",
        "label": "Vínculo",
        "type": FieldType.Relation,
        "required": true,
        "visible": true,
        "sizeMD": 12,
        "sizeSM": 12,
        "options": [
            "Cônjuge",
            "Filho(a)",
            "Pai / Mãe",
            "Irmão / Irmã",
            "Sobrinho / Sobrinha",
            "Tio / Tia",
            "Parentes de 4ª Grau Vivos",
        ],
        "defaultValue": "Filho(a)",
    },
]

export const ModelPessoaInventario = [
    {
        "field": "id",
        "label": "ID",
        "type": FieldType.Number,
        "required": false,
        "visible": false,
        "defaultValue": null,
    },
    {
        "field": "nome",
        "label": "Nome",
        "type": FieldType.String,
        "required": true,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": "",
    },
    // Regra: Data de Nascimento obrigatória apenas se a pessoa está viva (esta_vivo). Não se aplica ao De Cujus (ModelPessoaDeCujus).
    {
        "field": "data_nascimento",
        "label": "Data de Nascimento",
        "type": FieldType.Date,
        "required": "esta_vivo",
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": null,
    },
    {
        "field": "eh_incapaz",
        "label": "É Incapaz",
        "type": FieldType.Boolean,
        "required": false,
        "visible": true,
        "sizeMD": 3,
        "sizeSM": 6,
        "defaultValue": false,
    },
    {
        "field": "esta_vivo",
        "essential": true,
        "label": "Está Vivo",
        "type": FieldType.Boolean,
        "required": false,
        "visible": true,
        "sizeMD": 3,
        "sizeSM": 6,
        "defaultValue": true,
    },
    {
        "field": "cpf",
        "label": "CPF",
        "type": FieldType.String,
        "required": false,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": "",
    },
    {
        "field": "rg",
        "label": "RG",
        "type": FieldType.String,
        "required": false,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": "",
    },
    {
        "field": "profissao",
        "label": "Profissão",
        "type": FieldType.String,
        "required": false,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": "",
    },
    {
        "field": "endereco",
        "label": "Endereço",
        "type": FieldType.Address,
        "required": false,
        "visible": true,
        "sizeMD": 12,
        "sizeSM": 12,
        "defaultValue": "",
    },
    {
        "field": "estado_civil",
        "label": "Estado Civil",
        "type": FieldType.Options,
        "required": false,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "options": [
            {
                "label": "Casado(a)",
                "value": "casado",
            },
            {
                "label": "Divorciado(a)",
                "value": "divorciado",
            },
            {
                "label": "União estável",
                "value": "uniao_estavel",
            },
            {
                "label": "Solteiro(a)",
                "value": "solteiro",
            },
            {
                "label": "Viúvo(a)",
                "value": "viuvo",
            },
        ],
        "defaultValue": "solteiro",
    },
]

export const ModelDadosProcesso = [
    {
        "field": "comarca",
        "label": "Comarca",
        "type": FieldType.String,
        "required": false,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": "",
    },
    {
        "field": "vara",
        "label": "Vara (ex.: Vara de Família e Sucessões)",
        "type": FieldType.String,
        "required": false,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": "",
    },
    {
        "field": "numero_processo_cnj",
        "label": "Número do Processo (CNJ)",
        "type": FieldType.String,
        "required": false,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": "",
    },
    {
        "field": "tribunal",
        "label": "Tribunal",
        "type": FieldType.String,
        "required": false,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": "",
    },
]

export const ModelImovel = [
    // Campos comuns
    {
        "field": "id",
        "label": "ID",
        "type": FieldType.Number,
        "required": true,
        "visible": false,
        "defaultValue": null,
    },
    {
        "field": "descricao",
        "label": "Descrição",
        "type": FieldType.String,
        "required": true,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": "",
    },
    {
        "field": "tipo_imovel",
        "label": "Tipo do Imóvel",
        "type": FieldType.Options,
        "required": true,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "options": [
            "Urbano",
            "Rural",
            "Comercial",
            "Outro",
        ],
        "defaultValue": "Urbano",
    },
    {
        "field": "endereco",
        "label": "Endereço",
        "type": FieldType.Address,
        "required": true,
        "visible": true,
        "sizeMD": 12,
        "sizeSM": 12,
        "defaultValue": "",
    },
    {
        "field": "e_bem_particular",
        "label": "É bem particular?",
        "tooltip": bem_particular_tooltip,
        "type": FieldType.Boolean,
        "required": false,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 6,
        "defaultValue": false,
    },

    // Valor do Imóvel
    {
        "label": "Valor do Imóvel",
        "description": imovel_valor_separator_desc,
        "type": FieldType.Separator,
        "sizeMD": 12,
        "sizeSM": 12,
    },
    {
        "field": "valor",
        "label": "Valor venal do imóvel na data do óbito",
        "tooltip": imovel_venal_obito_tooltip,
        "type": FieldType.Money,
        "required": true,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": 0,
    },
    {
        "field": "valor_historico",
        "label": "Valor venal do imóvel atualizado",
        "tooltip": imovel_venal_atualizado_tooltip,
        "type": FieldType.Money,
        "required": true,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": 0,
    },
    {
        "field": "valor_base_escritura",
        "label": "Valor declarado na escritura / IR (partilha amigável)",
        "tooltip": imovel_valor_base_escritura_tooltip,
        "type": FieldType.Money,
        "required": false,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": 0,
    },
    {
        "field": "percentual_participacao",
        "label": "Percentual a ser inventariado",
        "type": FieldType.Percentage,
        "required": true,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": 100,
    },
    {
        "field": "tipo_restricao",
        "label": "Tipo de Restrição",
        "type": FieldType.Options,
        "required": true,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "options": [
            "Não possui",
            "Penhora",
            "Hipoteca",
            "Alienação fiduciária",
            "Gravame",
            "Outras restrições",
        ],
        "defaultValue": "Não possui",
    },

    // Dividas
    {
        "label": "Dívida",
        "description": "",
        "type": FieldType.Separator,
        "sizeMD": 12,
        "sizeSM": 12,
    },
    {
        "field": "esta_financiado",
        "label": "Está Financiado",
        "type": FieldType.Boolean,
        "required": false,
        "visible": true,
        "sizeMD": 2,
        "sizeSM": 12,
        "defaultValue": false,
    },
    {
        "field": "seguro_prestamista",
        "label": "Seguro Prestamista",
        "type": FieldType.Boolean,
        "required": false,
        "visible": "esta_financiado === true",
        "sizeMD": 2,
        "sizeSM": 12,
        "defaultValue": false,
    },
    {
        "field": "saldo_devedor",
        "label": "Saldo Devedor",
        "type": FieldType.Money,
        "required": true,
        "visible": "esta_financiado === true",
        "sizeMD": 4,
        "sizeSM": 12,
        "defaultValue": 0,
    },
    {
        "field": "data_quitacao",
        "label": "Data de Quitação",
        "type": FieldType.Date,
        "required": true,
        "visible": "esta_financiado === true",
        "sizeMD": 4,
        "sizeSM": 12,
        "defaultValue": null,
    },

    // Observações
    {
        "label": "Observações",
        "description": "",
        "type": FieldType.Separator,
        "sizeMD": 12,
        "sizeSM": 12,
    },
    {
        "field": "observacao",
        "label": "",
        "type": FieldType.LongString,
        "required": false,
        "visible": true,
        "sizeMD": 12,
        "sizeSM": 12,
        "defaultValue": "",
    },
]

export const ModelVeiculo = [
    // Campos comuns
    {
        "field": "id",
        "label": "ID",
        "type": FieldType.Number,
        "required": true,
        "visible": false,
        "defaultValue": null,
    },
    {
        "field": "descricao",
        "label": "Descrição",
        "type": FieldType.String,
        "required": true,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": "",
    },
    {
        "field": "tipo_veiculo",
        "label": "Tipo do veículo",
        "type": FieldType.Options,
        "required": false,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "options": [
            "Aeronave",
            "Barco/Lancha",
            "Terrestre (Carro/Moto/Caminhão)",
            "Trator / Colheitadeira",
            "Outro",
        ],
        "defaultValue": "Terrestre (Carro/Moto/Caminhão)",
    },
    {
        "field": "e_bem_particular",
        "label": "É bem particular?",
        "tooltip": bem_particular_tooltip,
        "type": FieldType.Boolean,
        "required": false,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 6,
        "defaultValue": false,
    },

    //  Valor do Veículo
    {
        "label": "Valor do Veículo",
        "description": veiculo_valor_separator_desc,
        "type": FieldType.Separator,
        "sizeMD": 12,
        "sizeSM": 12,
    },
    {
        "field": "valor",
        "label": "Valor de mercado na data do óbito",
        "tooltip": veiculo_valor_mercado_tooltip,
        "type": FieldType.Money,
        "required": true,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": 0,
    },
    {
        "field": "valor_base_escritura",
        "label": "Valor declarado na escritura / IR (partilha amigável)",
        "tooltip": veiculo_valor_base_escritura_tooltip,
        "type": FieldType.Money,
        "required": false,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": 0,
    },
    {
        "field": "tipo_restricao",
        "label": "Tipo de Restrição",
        "type": FieldType.Options,
        "required": true,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "options": [
            "Não possui",
            "Penhora",
            "Hipoteca",
            "Alienação fiduciária",
            "Gravame",
            "Outras restrições",
        ],
        "defaultValue": "Não possui",
    },

    // Dividas
    {
        "label": "Dívida",
        "description": "",
        "type": FieldType.Separator,
        "sizeMD": 12,
        "sizeSM": 12,
    },
    {
        "field": "esta_financiado",
        "label": "Está Financiado",
        "type": FieldType.Boolean,
        "required": false,
        "visible": true,
        "sizeMD": 2,
        "sizeSM": 12,
        "defaultValue": false,
    },
    {
        "field": "seguro_prestamista",
        "label": "Seguro Prestamista",
        "type": FieldType.Boolean,
        "required": false,
        "visible": "esta_financiado === true",
        "sizeMD": 2,
        "sizeSM": 12,
        "defaultValue": false,
    },
    {
        "field": "saldo_devedor",
        "label": "Saldo Devedor",
        "type": FieldType.Money,
        "required": true,
        "visible": "esta_financiado === true",
        "sizeMD": 4,
        "sizeSM": 12,
        "defaultValue": 0,
    },
    {
        "field": "data_quitacao",
        "label": "Data de Quitação",
        "type": FieldType.Date,
        "required": true,
        "visible": "esta_financiado === true",
        "sizeMD": 4,
        "sizeSM": 12,
        "defaultValue": null,
    },

    // Observações
    {
        "label": "Observações",
        "description": "",
        "type": FieldType.Separator,
        "sizeMD": 12,
        "sizeSM": 12,
    },
    {
        "field": "observacao",
        "label": "",
        "type": FieldType.LongString,
        "required": false,
        "visible": true,
        "sizeMD": 12,
        "sizeSM": 12,
        "defaultValue": "",
    },
]

export const ModelInvestimento = [
    // Campos comuns
    {
        "field": "id",
        "label": "ID",
        "type": FieldType.Number,
        "required": true,
        "visible": false,
        "defaultValue": null,
    },
    {
        "field": "descricao",
        "label": "Descrição",
        "type": FieldType.String,
        "required": true,
        "visible": true,
        "sizeMD": 12,
        "sizeSM": 12,
        "defaultValue": "",
    },
    {
        "field": "e_bem_particular",
        "label": "É bem particular?",
        "tooltip": bem_particular_tooltip,
        "type": FieldType.Boolean,
        "required": false,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 6,
        "defaultValue": false,
    },

    // tipo ativo
    {
        "label": "Tipo do Ativo",
        "description": "Selecione o tipo do ativo",
        "type": FieldType.Separator,
        "visible": true,
        "sizeMD": 12,
        "sizeSM": 12,
    },
    {
        "field": "tipo_ativo",
        "label": "Tipo de Ativo",
        "type": FieldType.Options,
        "required": false,
        "visible": true,
        "sizeMD": 12,
        "sizeSM": 12,
        "options": [
            {
                "label": "Ações",
                "value": "acoes",
            },
            {
                "label": "Ativos negociados em bolsa no Brasil (exceto ações)",
                "value": "ativos_bolsa_brasil",
            },
            {
                "label": "Consórcio não contemplado",
                "value": "consorcio_nao_contemplado",
            },
            {
                "label": "Conta Bancária",
                "value": "conta_bancaria",
            },
            {
                "label": "Demais Fundos de Índice de Mercado (ETFs)",
                "value": "demais_fundos_indice_mercado",
            },
            {
                "label": "Dinheiro",
                "value": "dinheiro",
            },
            {
                "label": "Fundos de Investimento Imobiliário (FII)",
                "value": "fundos_investimento_imobiliario",
            },
            {
                "label": "Fundos de Investimento em Ações - Mercado de Acesso",
                "value": "fundos_investimento_acoes_mercado_acesso",
            },
            {
                "label": "Furdos de Investimento em Ações e Fundos Mútuos de PrivatizaçSo - FGTS",
                "value": "fundos_investimento_acoes_fgts",
            },
            {
                "label": "Fundos de Investimento em Direitos Creditórios (FIDC)",
                "value": "fundos_investimento_direitos_creditorios",
            },
            {
                "label": "Fundos de Investirnento em Participações, Furdos de Investimento em Cotas de Fundos de Investimento em em Infraestrutura (FIP-IE)",
                "value": "fundos_investimento_participacoes_fip_ie",
            },
            {
                "label": "Fundos de Investimento nas Cadeias Produtivas Agroindustriais (Fiagro)",
                "value": "fundos_fiagro",
            },
            {
                "label": "Fundos de Investimento sem Tributação Periódica",
                "value": "fundos_investimento_sem_tributacao_periodica",
            },
            {
                "label": "Fundos de Investimentos Sujeitos à Tributação Periódica (come-cotas). Ex: Fundos de renda fixa, DI, Multimercado, Cambial, Renda Variável",
                "value": "fundos_investimento_sujeitos_tributacao_periodica",
            },
            {
                "label": "Fundos de Investimento de Curto Prazo",
                "value": "fundos_investimento_curto_prazo",
            },
            {
                "label": "Fundos de Investimento de Longo Prazo",
                "value": "fundos_investimento_longo_prazo",
            },
            {
                "label": "Fundos de Índice de Renda Fixa - Lei 13.043/14",
                "value": "fundos_indice_renda_fixa",
            },
            {
                "label": "Títulos de Renda Fixa (CDB. Tesouro Direto, LH, LC, etc)",
                "value": "titulos_renda_fixa",
            },
            {
                "label": "Títulos de Renda Fixa Isentos (CRI, CRA LCI, LCA, etc): isentos de IR",
                "value": "titulos_renda_fixa_isentos",
            },
            {
                "label": "Outros Fundos",
                "value": "outros_fundos",
            },
            {
                "label": "Previdência Privada",
                "value": "previdencia_privada",
            },
            {
                "label": "Outros",
                "value": "outros",
            },
        ],
        "defaultValue": "acoes",
    },

    //  Informações do Investimento
    {
        "label": "Informações do Investimento",
        "description": "",
        "type": FieldType.Separator,
        "sizeMD": 12,
        "sizeSM": 12,
    },

    // ativos comuns
    {
        "field": "valor",
        "label": "Valor",
        "type": FieldType.Money,
        "required": true,
        "visible": "tipo_ativo !== 'consorcio_nao_contemplado' && tipo_ativo !== 'conta_bancaria' && tipo_ativo !== 'previdencia_privada'",
        "sizeMD": 12,
        "sizeSM": 12,
        "defaultValue": 0,
    },

    // consorcio_nao_contemplado
    {
        "field": "valor_pago_cotas",
        "label": "Valor total pago pelas cotas",
        "type": FieldType.Money,
        "required": true,
        "visible": "tipo_ativo === 'consorcio_nao_contemplado'",
        "sizeMD": 12,
        "sizeSM": 12,
        "defaultValue": 0,
    },

    // conta_bancaria
    {
        "field": "tipo_conta",
        "label": "Tipo de Conta",
        "type": FieldType.Options,
        "required": false,
        "visible": "tipo_ativo === 'conta_bancaria'",
        "sizeMD": 6,
        "sizeSM": 12,
        "options": [
            {
                "label": "Conta Corrente",
                "value": "conta_corrente",
            },
            {
                "label": "Conta Poupança",
                "value": "conta_poupanca",
            },
        ],
        "defaultValue": "conta_corrente",
    },
    {
        "field": "banco",
        "label": "Banco",
        "type": FieldType.String,
        "required": true,
        "visible": "tipo_ativo === 'conta_bancaria'",
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": "",
    },
    {
        "field": "conta_bancaria_saldo",
        "label": "Saldo atual pertencente ao proprietário",
        "tooltip": conta_saldo_data_obito_tooltip,
        "type": FieldType.Money,
        "required": true,
        "visible": "tipo_ativo === 'conta_bancaria'",
        "sizeMD": 12,
        "sizeSM": 12,
        "defaultValue": 0,
    },
    {
        "field": "valor_base_escritura",
        "label": "Valor declarado na escritura / IR (partilha amigável)",
        "tooltip": conta_valor_base_escritura_tooltip,
        "type": FieldType.Money,
        "required": false,
        "visible": "tipo_ativo !== 'previdencia_privada'",
        "sizeMD": 12,
        "sizeSM": 12,
        "defaultValue": 0,
    },

    // previdencia_privada
    {
        "field": "tipo_previdencia",
        "label": "Tipo de Previdência",
        "type": FieldType.Options,
        "required": false,
        "visible": "tipo_ativo === 'previdencia_privada'",
        "sizeMD": 6,
        "sizeSM": 12,
        "options": [
            {
                "label": "VGBL",
                "value": "vgbl",
            },
            {
                "label": "PGBL",
                "value": "pgbl",
            },
        ],
        "defaultValue": "vgbl",
    },
    {
        "field": "previdencia_saldo",
        "label": "Reserva acumulada / provisão matemática / saldo atual",
        "type": FieldType.Money,
        "required": true,
        "visible": "tipo_ativo === 'previdencia_privada'",
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": 0,
    },
    {
        "field": "isencao_itcmd",
        "label": "Considerar isenção de ITCM sobre esta previdência?",
        "type": FieldType.Boolean,
        "required": false,
        "visible": "tipo_ativo === 'previdencia_privada'",
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": false,
    },
    {
        "field": "nomeados_beneficiarios",
        "label": "Foram nomeados beneficiários na contratação?",
        "type": FieldType.Boolean,
        "required": false,
        "visible": "tipo_ativo === 'previdencia_privada'",
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": false,
    },

    // Observações
    {
        "label": "Observações",
        "description": "",
        "type": FieldType.Separator,
        "sizeMD": 12,
        "sizeSM": 12,
    },
    {
        "field": "observacao",
        "label": "",
        "type": FieldType.LongString,
        "required": false,
        "visible": true,
        "sizeMD": 12,
        "sizeSM": 12,
        "defaultValue": "",
    },
]

export const ModelParticipacaoSocietaria = [
    // Campos comuns
    {
        "field": "id",
        "label": "ID",
        "type": FieldType.Number,
        "required": true,
        "visible": false,
        "defaultValue": null,
    },
    {
        "field": "descricao",
        "label": "Empresa",
        "type": FieldType.String,
        "required": true,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": "",
    },
    {
        "field": "cnpj",
        "label": "CNPJ",
        "type": FieldType.String,
        "required": true,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": "",
    },

    // de_cujus
    {
        "field": "valor",
        "label": "Valor Patrimonial",
        "type": FieldType.Money,
        "required": true,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 6,
        "defaultValue": 0,
    },
    {
        "field": "capital_social",
        "label": "Capital Social",
        "type": FieldType.Money,
        "required": true,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 6,
        "defaultValue": 0,
    },
    {
        "field": "percentual_participacao",
        "label": "Percentual de Participação",
        "type": FieldType.Percentage,
        "required": true,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 6,
        "defaultValue": 100,
    },
    {
        "field": "e_bem_particular",
        "label": "É bem particular?",
        "tooltip": bem_particular_tooltip,
        "type": FieldType.Boolean,
        "required": false,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 6,
        "defaultValue": false,
    },

    // Observações
    {
        "label": "Observações",
        "description": "",
        "type": FieldType.Separator,
        "sizeMD": 12,
        "sizeSM": 12,
    },
    {
        "field": "observacao",
        "label": "",
        "type": FieldType.LongString,
        "required": false,
        "visible": true,
        "sizeMD": 12,
        "sizeSM": 12,
        "defaultValue": "",
    },
]

export const ModelCredito = [
    {
        "field": "id",
        "label": "ID",
        "type": FieldType.Number,
        "required": true,
        "visible": false,
        "defaultValue": null,
    },
    {
        "field": "descricao",
        "label": "Descrição",
        "type": FieldType.String,
        "required": true,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": "",
    },
    {
        "field": "valor",
        "label": "Valor",
        "type": FieldType.Money,
        "required": true,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": 0,
    },
    {
        "field": "e_bem_particular",
        "label": "É bem particular?",
        "tooltip": bem_particular_tooltip,
        "type": FieldType.Boolean,
        "required": false,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 6,
        "defaultValue": false,
    },

    // Observações
    {
        "label": "Observações",
        "description": "",
        "type": FieldType.Separator,
        "sizeMD": 12,
        "sizeSM": 12,
    },
    {
        "field": "observacao",
        "label": "",
        "type": FieldType.LongString,
        "required": false,
        "visible": true,
        "sizeMD": 12,
        "sizeSM": 12,
        "defaultValue": "",
    },
]

export const ModelCriptomoeda = [
    {
        "field": "id",
        "label": "ID",
        "type": FieldType.Number,
        "required": true,
        "visible": false,
        "defaultValue": null,
    },
    {
        "field": "descricao",
        "label": "Descrição",
        "type": FieldType.String,
        "required": true,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 6,
        "defaultValue": "",
    },
    {
        "field": "tipo_cripto",
        "label": "Tipo do bem",
        "type": FieldType.Options,
        "required": false,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 6,
        "options": [
            "Criptomoeda",
            "NFT",
            "Outro",
        ],
        "defaultValue": "Criptomoeda",
    },
    {
        "field": "valor",
        "label": "Valor",
        "type": FieldType.Money,
        "required": true,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 6,
        "defaultValue": 0,
    },
    {
        "field": "e_bem_particular",
        "label": "É bem particular?",
        "tooltip": bem_particular_tooltip,
        "type": FieldType.Boolean,
        "required": false,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 6,
        "defaultValue": false,
    },

    // Observações
    {
        "label": "Observações",
        "description": "",
        "type": FieldType.Separator,
        "sizeMD": 12,
        "sizeSM": 12,
    },
    {
        "field": "observacao",
        "label": "",
        "type": FieldType.LongString,
        "required": false,
        "visible": true,
        "sizeMD": 12,
        "sizeSM": 12,
        "defaultValue": "",
    },
]

export const ModelOutroBem = [
    {
        "field": "id",
        "label": "ID",
        "type": FieldType.Number,
        "required": true,
        "visible": false,
        "defaultValue": null,
    },
    {
        "field": "descricao",
        "label": "Descrição",
        "type": FieldType.String,
        "required": true,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": "",
    },
    {
        "field": "valor",
        "label": "Valor",
        "type": FieldType.Money,
        "required": true,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": 0,
    },
    {
        "field": "tipo_ativo",
        "label": "Tipo de Ativo",
        "type": FieldType.Options,
        "required": false,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "options": [
            {
                "label": "Direitos Autorais",
                "value": "direitos_autorais",
            },
            {
                "label": "Jóia / Quadro / Objeto de Arte",
                "value": "joia_quadro_arte",
            },
            {
                "label": "Maquinário e Equipamentos",
                "value": "maquinario",
            },
            {
                "label": "Semoventes",
                "value": "semovente",
            },
            {
                "label": "Outros",
                "value": "outro",
            },
        ],
        "defaultValue": "direitos_autorais",
    },

    // semovente
    {
        "field": "categoria_semovente",
        "label": "Categoria",
        "type": FieldType.Options,
        "required": false,
        "visible": "tipo_ativo === 'semovente'",
        "sizeMD": 6,
        "sizeSM": 12,
        "options": [
            "Equino",
            "Gado",
            "Suíno",
            "Outros",
        ],
        "defaultValue": "Equino",
    },

    // maquinario
    {
        "field": "categoria_maquinario",
        "label": "Categoria",
        "type": FieldType.Options,
        "required": false,
        "visible": "tipo_ativo === 'maquinario'",
        "sizeMD": 6,
        "sizeSM": 12,
        "options": [
            "Colheitadeira",
            "Maquinário",
            "Trator",
            "Outros",
        ],
        "defaultValue": "Colheitadeira",
    },
    {
        "field": "tipo_restricao",
        "label": "Tipo de Restrição",
        "type": FieldType.Options,
        "required": true,
        "visible": "tipo_ativo === 'maquinario'",
        "sizeMD": 6,
        "sizeSM": 12,
        "options": [
            "Não possui",
            "Penhora",
            "Hipoteca",
            "Alienação fiduciária",
            "Gravame",
            "Outras restrições",
        ],
        "defaultValue": "Não possui",
    },

    {
        "field": "e_bem_particular",
        "label": "É bem particular?",
        "tooltip": bem_particular_tooltip,
        "type": FieldType.Boolean,
        "required": false,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 6,
        "defaultValue": false,
    },

    // Dividas
    {
        "label": "Dívida",
        "description": "",
        "type": FieldType.Separator,
        "sizeMD": 12,
        "sizeSM": 12,
    },
    {
        "field": "esta_financiado",
        "label": "Está Financiado",
        "type": FieldType.Boolean,
        "required": false,
        "visible": true,
        "sizeMD": 2,
        "sizeSM": 12,
        "defaultValue": false,
    },
    {
        "field": "seguro_prestamista",
        "label": "Seguro Prestamista",
        "type": FieldType.Boolean,
        "required": false,
        "visible": "esta_financiado === true",
        "sizeMD": 2,
        "sizeSM": 12,
        "defaultValue": false,
    },
    {
        "field": "saldo_devedor",
        "label": "Saldo Devedor",
        "type": FieldType.Money,
        "required": true,
        "visible": "esta_financiado === true",
        "sizeMD": 4,
        "sizeSM": 12,
        "defaultValue": 0,
    },
    {
        "field": "data_quitacao",
        "label": "Data de Quitação",
        "type": FieldType.Date,
        "required": true,
        "visible": "esta_financiado === true",
        "sizeMD": 4,
        "sizeSM": 12,
        "defaultValue": null,
    },

    // Observações
    {
        "label": "Observações",
        "description": "",
        "type": FieldType.Separator,
        "sizeMD": 12,
        "sizeSM": 12,
    },
    {
        "field": "observacao",
        "label": "",
        "type": FieldType.LongString,
        "required": false,
        "visible": true,
        "sizeMD": 12,
        "sizeSM": 12,
        "defaultValue": "",
    },
]

export const ModelSeguro = [
    // Campos comuns
    {
        "field": "id",
        "label": "ID",
        "type": FieldType.Number,
        "required": true,
        "visible": false,
        "defaultValue": null,
    },
    {
        "field": "descricao",
        "label": "Descrição",
        "type": FieldType.String,
        "required": true,
        "visible": true,
        "sizeMD": 8,
        "sizeSM": 12,
        "defaultValue": "",
    },
    {
        "field": "tem_beneficiarios",
        "label": "Foram nomeados beneficiários durante a contratação?",
        "type": FieldType.Boolean,
        "required": false,
        "visible": true,
        "sizeMD": 4,
        "sizeSM": 12,
        "defaultValue": false,
    },
    {
        "field": "tipo_seguro",
        "label": "Tipo de Seguro",
        "type": FieldType.Options,
        "required": true,
        "visible": true,
        "sizeMD": 12,
        "sizeSM": 12,
        "options": [
            {
                "label": "Comum",
                "value": "comum",
            },
            {
                "label": "Resgatável",
                "value": "resgatavel",
            },
            {
                "label": "Temporário",
                "value": "temporario",
            },
        ],
        "defaultValue": "comum",
    },
    {
        "field": "valor",
        "label": "Cobertura do Seguro",
        "type": FieldType.Money,
        "required": true,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": 0,
    },
    {
        "field": "valor_premio_mensal",
        "label": "Prêmio Mensal",
        "type": FieldType.Money,
        "required": true,
        "visible": "tipo_seguro === 'comum' || tipo_seguro === 'temporario'",
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": 0,
    },

    // resgatavel
    {
        "field": "valor_premio",
        "label": "Valor do Prêmio",
        "type": FieldType.Money,
        "required": true,
        "visible": "tipo_seguro === 'resgatavel'",
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": 0,
    },
    {
        "field": "pagamento_premio",
        "label": "Pagamento do Prêmio",
        "type": FieldType.Money,
        "required": true,
        "visible": "tipo_seguro === 'resgatavel'",
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": 0,
    },
    {
        "field": "valor_restate_liquido",
        "label": "Valor de Resgate Líquido",
        "type": FieldType.Money,
        "required": true,
        "visible": "tipo_seguro === 'resgatavel'",
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": 0,
    },
    {
        "field": "restage_apos_vencimento",
        "label": "Resgate Após Vencimento",
        "type": FieldType.Boolean,
        "required": false,
        "visible": "tipo_seguro === 'resgatavel'",
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": false,
    },

    // resgatavel e temporario
    {
        "field": "data_vencimento",
        "label": "Data de Vencimento do Seguro",
        "type": FieldType.Date,
        "required": true,
        "visible": "tipo_seguro !== 'comum'",
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": null,
    },

    {
        "field": "e_bem_particular",
        "label": "É bem particular?",
        "tooltip": bem_particular_tooltip,
        "type": FieldType.Boolean,
        "required": false,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 6,
        "defaultValue": false,
    },

    // Observações
    {
        "label": "Observações",
        "description": "",
        "type": FieldType.Separator,
        "sizeMD": 12,
        "sizeSM": 12,
    },
    {
        "field": "observacao",
        "label": "",
        "type": FieldType.LongString,
        "required": false,
        "visible": true,
        "sizeMD": 12,
        "sizeSM": 12,
        "defaultValue": "",
    },
]

export const ModelBemInternacional = [
    {
        "field": "id",
        "label": "ID",
        "type": FieldType.Number,
        "required": true,
        "visible": false,
        "defaultValue": null,
    },
    {
        "field": "descricao",
        "label": "Descrição",
        "type": FieldType.String,
        "required": true,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": "",
    },
    {
        "field": "tipo_bem",
        "label": "Tipo do Bem",
        "type": FieldType.Options,
        "required": false,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "options": [
            "Imóvel",
            "Participação societária",
            "Veículo",
            "Outros",
        ],
        "defaultValue": "Imóvel",
    },
    {
        "field": "valor",
        "label": "Valor",
        "type": FieldType.Money,
        "required": true,
        "visible": true,
        "sizeMD": 12,
        "sizeSM": 12,
        "defaultValue": 0,
    },
    {
        "field": "endereco",
        "label": "Endereço",
        "type": FieldType.InternationalAddress,
        "required": true,
        "visible": true,
        "sizeMD": 12,
        "sizeSM": 12,
        "defaultValue": "",
    },

    {
        "field": "e_bem_particular",
        "label": "É bem particular?",
        "tooltip": bem_particular_tooltip,
        "type": FieldType.Boolean,
        "required": false,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 6,
        "defaultValue": false,
    },

    // Dividas
    {
        "label": "Dívida",
        "description": "",
        "type": FieldType.Separator,
        "sizeMD": 12,
        "sizeSM": 12,
    },
    {
        "field": "esta_financiado",
        "label": "Está Financiado",
        "type": FieldType.Boolean,
        "required": false,
        "visible": true,
        "sizeMD": 2,
        "sizeSM": 12,
        "defaultValue": false,
    },
    {
        "field": "seguro_prestamista",
        "label": "Seguro Prestamista",
        "type": FieldType.Boolean,
        "required": false,
        "visible": "esta_financiado === true",
        "sizeMD": 2,
        "sizeSM": 12,
        "defaultValue": false,
    },
    {
        "field": "saldo_devedor",
        "label": "Saldo Devedor",
        "type": FieldType.Money,
        "required": true,
        "visible": "esta_financiado === true",
        "sizeMD": 4,
        "sizeSM": 12,
        "defaultValue": 0,
    },
    {
        "field": "data_quitacao",
        "label": "Data de Quitação",
        "type": FieldType.Date,
        "required": true,
        "visible": "esta_financiado === true",
        "sizeMD": 4,
        "sizeSM": 12,
        "defaultValue": null,
    },

    // Observações
    {
        "label": "Observações",
        "description": "",
        "type": FieldType.Separator,
        "sizeMD": 12,
        "sizeSM": 12,
    },
    {
        "field": "observacao",
        "label": "",
        "type": FieldType.LongString,
        "required": false,
        "visible": true,
        "sizeMD": 12,
        "sizeSM": 12,
        "defaultValue": "",
    },
]

export const ModelDivida = [
    {
        "field": "id",
        "label": "ID",
        "type": FieldType.Number,
        "required": true,
        "visible": false,
        "defaultValue": null,
    },

    {
        "field": "descricao",
        "label": "Descrição",
        "type": FieldType.String,
        "required": true,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": "",
    },
    {
        "field": "tipo_divida",
        "label": "Tipo da Dívida",
        "type": FieldType.Options,
        "required": false,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "description": "Define como o lançamento afeta o cálculo: 'Abate' reduz o patrimônio (Art. 1.847 CC); 'Tributo pago' substitui a estimativa fiscal do sistema; 'Informativa' é histórico (não afeta totais); 'Ignorada' fica fora do escopo deste inventário.",
        "options": [
            {
                "label": "Empréstimo (abate patrimônio)",
                "value": "emprestimo"
            },
            {
                "label": "Outros (abate patrimônio)",
                "value": "outros",
            },
            {
                "label": "Despesa do Inventário (genérica, abate patrimônio)",
                "value": "despesa_inventario",
            },
            {
                "label": "Despesa de Funeral (abate patrimônio – Art. 1.847 CC)",
                "value": "despesa_funeral",
            },
            {
                "label": "Tributo do Inventário pago (substitui ITCMD estimado)",
                "value": "tributo_inventario_pago",
            },
            {
                "label": "Cartório do Inventário pago (informativa, não abate)",
                "value": "cartorio_inventario_pago",
            },
            {
                "label": "Honorários advocatícios do Inventário pago (informativa, não abate)",
                "value": "honorarios_inventario_pago",
            },
            {
                "label": "Despesa de Inventário Anterior (ignorada no cálculo)",
                "value": "despesa_inventario_anterior",
            },
            {
                "label": "Despesa Pós-Partilha (ignorada no cálculo)",
                "value": "despesa_pos_partilha",
            },
            {
                "label": "Passivo de Bem (IPTU/IPVA atrasado, abate patrimônio)",
                "value": "passivo_bem",
            },
        ],
        "defaultValue": "outros",
    },

    // Dividas    
    {
        "field": "saldo_devedor",
        "label": "Saldo Devedor",
        "type": FieldType.Money,
        "required": true,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": 0,
    },
    {
        "field": "data_quitacao",
        "label": "Data de Quitação",
        "type": FieldType.Date,
        "required": true,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": null,
    },
    {
        "field": "seguro_prestamista",
        "label": "Seguro Prestamista",
        "type": FieldType.Boolean,
        "required": false,
        "visible": "tipo_divida === 'emprestimo' || tipo_divida === 'outros' || tipo_divida === 'despesa_inventario' || tipo_divida === 'despesa_funeral' || tipo_divida === 'passivo_bem'",
        "sizeMD": 3,
        "sizeSM": 12,
        "defaultValue": false,
    },

    // Observações
    {
        "label": "Observações",
        "description": "",
        "type": FieldType.Separator,
        "sizeMD": 12,
        "sizeSM": 12,
    },
    {
        "field": "observacao",
        "label": "",
        "type": FieldType.LongString,
        "required": false,
        "visible": true,
        "sizeMD": 12,
        "sizeSM": 12,
        "defaultValue": "",
    },
]

export const ModelDoacao = [
    {
        "field": "id",
        "label": "ID",
        "type": FieldType.Number,
        "required": true,
        "visible": false,
        "defaultValue": null,
    },
    {
        "label": "Bem doado",
        "description": "Liberalidade intervivos do(a) de cujus — registre aqui doações já formalizadas em vida. O doador é sempre o(a) de cujus; abaixo você indica quais familiares receberam (donatários) e a porcentagem de cada um.",
        "type": FieldType.Separator,
        "visible": true,
        "sizeMD": 12,
        "sizeSM": 12,
    },
    {
        "field": "bem_relacionado",
        "label": "Bem Doado",
        "type": FieldType.BemRelacionado,
        "required": false,
        "visible": true,
        "sizeMD": 12,
        "sizeSM": 12,
        "defaultValue": "",
    },
    {
        "field": "status_doacao",
        "label": "Status da Doação",
        "type": FieldType.Options,
        "required": true,
        "visible": "bem_relacionado.split(':')[0] === 'imovel' || bem_relacionado.split(':')[0] === 'participacoes_societarias'",
        "sizeMD": 12,
        "sizeSM": 12,
        "options": [
            {
                "label": "Doado",
                "value": "doado",
                "visible": "bem_relacionado.split(':')[0] !== 'imovel' && bem_relacionado.split(':')[0] !== 'participacoes_societarias'",
            },
            {
                "label": "Doado Sem Usufruto",
                "value": "doado_sem_usufruto",
                "visible": "bem_relacionado.contains('imovel:') || bem_relacionado.contains('participacoes_societarias:')",
            },
            {
                "label": "Doado Com Usufruto",
                "value": "doado_com_usufruto",
                "visible": "bem_relacionado.contains('imovel:') || bem_relacionado.contains('participacoes_societarias:')",
            },
        ],
        "defaultValue": "doado",
    },
    {
        "field": "valor",
        "label": "Valor na data do ato (referência atualizada)",
        "tooltip": doacao_valor_tooltip,
        "type": FieldType.Money,
        "required": true,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": 0,
    },
    {
        "field": "data_doacao",
        "label": "Data da Doação",
        "type": FieldType.Date,
        "required": true,
        "visible": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": null,
    },

    {
        "field": "_sp_usufruto_split",
        "label": "",
        "type": FieldType.DoacaoUsufrutoSpSplit,
        "required": false,
        "visible": "status_doacao === 'doado_com_usufruto' && (bem_relacionado.includes('imovel:') || bem_relacionado.includes('participacoes_societarias:'))",
        "sizeMD": 12,
        "sizeSM": 12,
        "defaultValue": null,
    },

    // Campos específicos para outros valores de status_propriedade 
    {
        "field": "percentual_nua_propriedade",
        "label": "Percentual da nua-propriedade no valor da liberalidade (SP: ⅔)",
        "tooltip": doacao_percentual_bem_tooltip,
        "type": FieldType.Percentage,
        "required": true,
        "visible": "status_doacao === 'doado_com_usufruto'",
        "readOnly": true,
        "sizeMD": 6,
        "sizeSM": 12,
        "defaultValue": 0,
    },
    {
        "label": "Donatários (quem recebeu)",
        "description": doacao_donatarios_desc,
        "type": FieldType.Separator,
        "visible": true,
        "sizeMD": 12,
        "sizeSM": 12,
    },
    {
        "field": "doacao_pessoas_inventario",
        "label": "Porcentagem da liberalidade por familiar (0% = não foi donatário)",
        "type": FieldType.DoacaoPessoaInventario,
        "required": true,
        "visible": true,
        "sizeMD": 12,
        "sizeSM": 12,
        "defaultValue": [],
    },

    // Observações
    {
        "label": "Observações",
        "description": "",
        "type": FieldType.Separator,
        "sizeMD": 12,
        "sizeSM": 12,
    },
    {
        "field": "observacao",
        "label": "",
        "type": FieldType.LongString,
        "required": false,
        "visible": true,
        "sizeMD": 12,
        "sizeSM": 12,
        "defaultValue": "",
    },
]

/** Legado: bem ou quantia deixada por testamento a um legatário (não é quota da herança). */
export const ModelLegado = [
    {
        "field": "id",
        "label": "ID",
        "type": FieldType.Number,
        "required": true,
        "visible": false,
        "defaultValue": null,
    },
    {
        "field": "pessoa_inventario_id",
        "label": "Legatário (pessoa que recebe o legado)",
        "type": "relationId" as any,
        "required": true,
        "visible": true,
        "sizeMD": 12,
        "sizeSM": 12,
        "defaultValue": null,
    },
    {
        "field": "tipo_legado",
        "label": "Tipo de legado",
        "type": FieldType.Options,
        "required": true,
        "visible": true,
        "sizeMD": 12,
        "sizeSM": 12,
        "options": [
            { "label": "Bem determinado", "value": "bem" },
            { "label": "Quantia em dinheiro", "value": "quantia" },
        ],
        "defaultValue": "bem",
    },
    {
        "field": "bem_relacionado",
        "label": "Bem legado",
        "type": FieldType.BemRelacionado,
        "required": false,
        "visible": "tipo_legado === 'bem'",
        "sizeMD": 12,
        "sizeSM": 12,
        "defaultValue": "",
    },
    {
        "field": "valor",
        "label": "Valor (R$)",
        "type": FieldType.Money,
        "required": false,
        "visible": "tipo_legado === 'quantia'",
        "sizeMD": 12,
        "sizeSM": 12,
        "defaultValue": 0,
    },
    {
        "field": "descricao",
        "label": "Descrição ou observação",
        "type": FieldType.String,
        "required": false,
        "visible": true,
        "sizeMD": 12,
        "sizeSM": 12,
        "defaultValue": "",
    },
]

/** Herdeiro testamentário: pessoa instituída no testamento com quota da parte disponível. */
export const ModelHerdeiroTestamentario = [
    {
        "field": "id",
        "label": "ID",
        "type": FieldType.Number,
        "required": true,
        "visible": false,
        "defaultValue": null,
    },
    {
        "field": "pessoa_inventario_id",
        "label": "Pessoa (herdeiro testamentário)",
        "type": "relationId" as any,
        "required": true,
        "visible": true,
        "sizeMD": 12,
        "sizeSM": 12,
        "defaultValue": null,
    },
    {
        "field": "quota_testamentaria",
        "label": "Quota testamentária (%)",
        "type": FieldType.Percentage,
        "required": true,
        "visible": true,
        "sizeMD": 12,
        "sizeSM": 12,
        "defaultValue": 0,
    },
]